import { useState } from "react";
import type { SupplierInvoice } from "@shared-core/data/supplier-portal-adapter";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  ACTION_WORDING,
  INVOICE_WORDING,
  dayMonth,
  invoiceStatus,
  pounds,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../primitives/button";
import { Card } from "../primitives/card";
import { Textarea } from "../primitives/textarea";
import { FilePicker } from "../patterns/FilePicker";
import { FormField } from "../patterns/FormField";
import { StatusPill } from "./StatusPill";
import { StaffViewReadOnly } from "./StaffViewReadOnly";

/**
 * A finance query on one invoice, answered here so the conversation stays with the invoice (brief decision 5):
 * the thread so far, the reply and an optional file.
 */
export function InvoiceQueryCard({
  invoice,
  onReply,
}: {
  invoice: SupplierInvoice;
  onReply: (input: { text: string; attachment: File | null }) => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const write = useFieldErrorWrite("We couldn't send your reply.");
  const id = `query-${invoice.invoice_ref}`;
  const status = invoiceStatus(invoice, { short: true });

  return (
    <Card
      aria-label={`Query on ${invoice.invoice_ref}`}
      className="gap-3 border-primary p-4 ring-[3px] ring-primary/15"
    >
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="flex-1 font-semibold">
          {invoice.invoice_ref} · {pounds(invoice.total_inc_vat)}
        </h2>
        <StatusPill label={status.label} tone={status.tone} />
      </div>
      {invoice.query?.messages.map((message, index) => (
        <blockquote
          key={index}
          className="border-l-[3px] px-2.5 py-1.5 text-sm text-muted-foreground"
        >
          <span className="italic">&ldquo;{message.text}&rdquo;</span>{" "}
          {message.author_name}, {dayMonth(message.at)}
          {message.attachment_file_name && (
            <span> · {message.attachment_file_name}</span>
          )}
        </blockquote>
      ))}
      <StaffViewReadOnly>
        <form
          noValidate
          className="grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void write.run(() => onReply({ text, attachment }));
          }}
        >
          {write.banner && (
            <p role="alert" className="text-sm text-red-600">
              {write.banner}
            </p>
          )}
          <FormField
            id={`${id}-reply`}
            label={INVOICE_WORDING.yourReply}
            error={write.fieldErrors.text}
          >
            {(control) => (
              <Textarea
                {...control}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            )}
          </FormField>
          {write.fieldErrors.attachment && (
            <p className="text-sm text-red-600">
              {write.fieldErrors.attachment}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <FilePicker
              id={`${id}-attachment`}
              compact
              file={attachment}
              onChange={setAttachment}
              accept="application/pdf,image/png,image/jpeg"
              prompt=""
              chooseLabel={ACTION_WORDING.attachFile}
            />
            <Button type="submit" disabled={write.isBusy}>
              {ACTION_WORDING.sendReply}
            </Button>
          </div>
        </form>
      </StaffViewReadOnly>
    </Card>
  );
}
